import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "onChange", "label", "minDate", "maxDate", "fullWidth", "slotProps", "open", "onOpen", "onClose"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker as MUIDatePicker } from '@mui/x-date-pickers/DatePicker';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useSelector } from 'react-redux';
import { translate as translateConstructor } from '@haldor/ui';
import { getNormalizedActiveLanguage, getPickersLocale } from 'helpers/locale';
import { usePickerAutoClose } from './usePickerAutoClose';

// Generate unique ID for each DatePicker instance
var datePickerIdCounter = 0;

/**
 * @typedef {Object} DatePickerProps
 * @property {*} value
 * @property {Function} onChange
 * @property {string} [label]
 * @property {*} [minDate]
 * @property {*} [maxDate]
 * @property {boolean} [fullWidth]
 * @property {Object} [slotProps]
 * @property {boolean} [open]
 * @property {Function} [onOpen]
 * @property {Function} [onClose]
 */

/**
 * DatePicker wrapper with auto-close support
 * @param {DatePickerProps} props
 * @returns {React.ReactElement}
 */
var DatePicker = function DatePicker(/** @type {any} */_ref) {
  var _pickersLocale$compon;
  var value = _ref.value,
    onChange = _ref.onChange,
    label = _ref.label,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    _ref$fullWidth = _ref.fullWidth,
    fullWidth = _ref$fullWidth === void 0 ? true : _ref$fullWidth,
    _ref$slotProps = _ref.slotProps,
    slotProps = _ref$slotProps === void 0 ? {} : _ref$slotProps,
    open = _ref.open,
    onOpen = _ref.onOpen,
    onClose = _ref.onClose,
    props = _objectWithoutProperties(_ref, _excluded);
  var translations = useSelector(function (state) {
    var _state$Languages;
    return (_state$Languages = state.Languages) === null || _state$Languages === void 0 ? void 0 : _state$Languages.translations;
  });
  var translate = React.useMemo(function () {
    return translateConstructor(translations);
  }, [translations]);
  var normalizedLang = React.useMemo(function () {
    return getNormalizedActiveLanguage();
  }, [translations]);
  var pickersLocale = React.useMemo(function () {
    return getPickersLocale(normalizedLang);
  }, [normalizedLang]);
  var localeText = pickersLocale === null || pickersLocale === void 0 || (_pickersLocale$compon = pickersLocale.components) === null || _pickersLocale$compon === void 0 || (_pickersLocale$compon = _pickersLocale$compon.MuiLocalizationProvider) === null || _pickersLocale$compon === void 0 || (_pickersLocale$compon = _pickersLocale$compon.defaultProps) === null || _pickersLocale$compon === void 0 ? void 0 : _pickersLocale$compon.localeText;
  var effectiveLabel = label ? translate(label) : undefined;
  var slotPropsAny = /** @type {any} */slotProps || {};
  var textFieldProps = slotPropsAny.textField || {};
  var actionBarProps = slotPropsAny.actionBar || {};
  var layoutProps = slotPropsAny.layout || {};
  var mergedTextFieldProps = _objectSpread(_objectSpread({}, textFieldProps), {}, {
    fullWidth: fullWidth,
    sx: _objectSpread(_objectSpread({}, textFieldProps.sx || {}), {}, {
      width: fullWidth ? '100%' : textFieldProps.sx && textFieldProps.sx.width,
      '& .MuiInputBase-root': _objectSpread({
        backgroundColor: '#fff'
      }, textFieldProps.sx && textFieldProps.sx['& .MuiInputBase-root'] || {})
    })
  });
  var mergedActionBarProps = _objectSpread({
    actions: ['cancel', 'accept']
  }, actionBarProps);
  var mergedLayoutProps = _objectSpread(_objectSpread({}, layoutProps), {}, {
    sx: _objectSpread(_objectSpread({}, layoutProps.sx || {}), {}, {
      '& .MuiDateCalendar-root': {
        height: 'auto',
        minHeight: 'auto'
      }
    })
  });

  // Ensure the picker's popper/dialog is on top of everything (modals, overlays)
  var TOP_Z_INDEX = 214748364;
  var mergedSlotProps = _objectSpread(_objectSpread({}, slotPropsAny), {}, {
    textField: mergedTextFieldProps,
    actionBar: mergedActionBarProps,
    layout: mergedLayoutProps,
    popper: _objectSpread(_objectSpread({}, slotPropsAny.popper || {}), {}, {
      sx: _objectSpread(_objectSpread({}, slotPropsAny.popper && slotPropsAny.popper.sx || {}), {}, {
        zIndex: TOP_Z_INDEX,
        fontSize: '1rem',
        transform: 'none',
        maxWidth: 'none',
        '.MuiClock-root, .MuiClockNumber-root, .MuiPickersSlideTransition-root': {
          fontSize: '1rem'
        }
      })
    }),
    dialog: _objectSpread(_objectSpread({}, slotPropsAny.dialog || {}), {}, {
      PaperProps: _objectSpread(_objectSpread({}, slotPropsAny.dialog && slotPropsAny.dialog.PaperProps || {}), {}, {
        sx: _objectSpread(_objectSpread({}, slotPropsAny.dialog && slotPropsAny.dialog.PaperProps && slotPropsAny.dialog.PaperProps.sx || {}), {}, {
          zIndex: TOP_Z_INDEX,
          fontSize: '1rem',
          transform: 'none',
          maxWidth: 'none'
        })
      })
    })
  });

  // Use auto-close hook if no controlled props provided
  var pickerIdRef = React.useRef("dp_".concat(++datePickerIdCounter));
  var _usePickerAutoClose = usePickerAutoClose(pickerIdRef.current),
    _usePickerAutoClose2 = _slicedToArray(_usePickerAutoClose, 2),
    autoCloseOpen = _usePickerAutoClose2[0],
    setAutoCloseOpen = _usePickerAutoClose2[1];

  // Allow parent control OR auto-close behavior
  var isControlled = open !== undefined;
  var actualOpen = isControlled ? open : autoCloseOpen;
  var handleOpen = function handleOpen() {
    if (isControlled && onOpen) onOpen();else setAutoCloseOpen(true);
  };
  var handleClose = function handleClose() {
    if (isControlled && onClose) onClose();else setAutoCloseOpen(false);
  };
  return /*#__PURE__*/React.createElement("div", {
    id: pickerIdRef.current
  }, /*#__PURE__*/React.createElement(GlobalStyles, {
    styles: {
      '.MuiPickersPopper-root, .MuiPickersModal-root, .MuiPickersDesktopPaper-root': {
        zIndex: '1000000000 !important'
      },
      '.MuiInputAdornment-root': {
        zIndex: '1000000001'
      }
    }
  }), /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterMoment,
    adapterLocale: normalizedLang,
    localeText: localeText
  }, /*#__PURE__*/React.createElement(MUIDatePicker, _extends({
    sx: {
      width: fullWidth ? '100%' : undefined
    },
    value: value,
    onChange: onChange,
    label: effectiveLabel,
    minDate: minDate,
    maxDate: maxDate,
    open: actualOpen,
    onOpen: handleOpen,
    onClose: handleClose,
    slotProps: mergedSlotProps
  }, props))));
};
export default DatePicker;